import React, { useState } from 'react';
import {
  CalendarDaysIcon,
  MapPinIcon,
  BanknotesIcon,
  UserGroupIcon,
  ClockIcon,
  TagIcon,
  PlayIcon,
  PauseIcon,
  StopIcon
} from '@heroicons/react/24/outline';
import { Campaign } from '../../types';
import { useAuthStore } from '../../store/authStore';
import { TruncatedText } from '../common/TruncatedText';
import { graphqlApi } from '../../services/graphql';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocaleFormatters } from '../../utils/localeFormatters';
import { usePermissions } from '../../hooks/usePermissions';

interface CampaignCardProps {
  campaign: Campaign;
  showApplyButton?: boolean;
  onViewDetails?: (campaign: Campaign) => void;
  onApply?: (campaign: Campaign) => void;
  onStatusChange?: (campaignId: string, newStatus: Campaign['status']) => void;
}

export const CampaignCard: React.FC<CampaignCardProps> = ({
  campaign,
  showApplyButton = false,
  onViewDetails,
  onApply,
  onStatusChange
}) => {
  const { user } = useAuthStore();
  const { hasPermission } = usePermissions();
  const [updating, setUpdating] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(campaign.status);
  const { t } = useTranslation('campaigns');
  const { formatDate, formatCurrency, formatNumber } = useLocaleFormatters();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleStatusChange = async (newStatus: Campaign['status']) => {
    if (updating || !user || newStatus === currentStatus) return;

    // Validate status transitions
    if (currentStatus === 'completed' || currentStatus === 'cancelled') {
      toast.error(t('campaigns:messages.cannotChangeCompletedStatus'));
      return;
    }

    setUpdating(true);
    try {
      // Create a minimal update payload with just the status change
      const updateData: any = {
        status: newStatus,
      };

      console.log('🔄 Updating campaign status:', {
        campaignId: campaign.campaignId,
        from: currentStatus,
        to: newStatus,
        updateData
      });

      const response = await graphqlApi.updateCampaign(campaign.campaignId, updateData);

      if (response.success) {
        setCurrentStatus(newStatus);
        onStatusChange?.(campaign.campaignId, newStatus);

        const statusMessages = {
          active: t('campaigns:messages.campaignActivated'),
          paused: t('campaigns:messages.campaignPaused'),
          draft: t('campaigns:messages.campaignDraft'),
          closed: t('campaigns:messages.campaignClosed')
        };

        toast.success(statusMessages[newStatus as keyof typeof statusMessages] || t('campaigns:messages.statusUpdated'));
      } else {
        toast.error(response.message || t('campaigns:messages.statusUpdateFailed'));
      }
    } catch (error: any) {
      console.error('Error updating campaign status:', error);
      toast.error(error.message || t('campaigns:messages.statusUpdateFailed'));
    } finally {
      setUpdating(false);
    }
  };

  const getStatusActions = () => {
    // Check if user has permission to update campaigns
    const canUpdateCampaign = hasPermission('campaigns:update');
    const isOwner = user?.userId === campaign.userId || user?.userId === campaign.createdBy;
    
    if (!canUpdateCampaign || !isOwner) return null;

    const actions = [];

    switch (currentStatus) {
      case 'draft':
        actions.push({
          label: t('campaigns:actions.activate'),
          icon: PlayIcon,
          action: () => handleStatusChange('active'),
          className: 'text-green-600 hover:text-green-800 hover:bg-green-50',
          tooltip: t('campaigns:actions.activateTooltip')
        });
        break;

      case 'active':
        actions.push({
          label: t('campaigns:actions.pause'),
          icon: PauseIcon,
          action: () => handleStatusChange('paused'),
          className: 'text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50',
          tooltip: t('campaigns:actions.pauseTooltip')
        });
        actions.push({
          label: t('campaigns:actions.close'),
          icon: StopIcon,
          action: () => handleStatusChange('completed'),
          className: 'text-red-600 hover:text-red-800 hover:bg-red-50',
          tooltip: t('campaigns:actions.closeTooltip')
        });
        break;

      case 'paused':
        actions.push({
          label: t('campaigns:actions.resume'),
          icon: PlayIcon,
          action: () => handleStatusChange('active'),
          className: 'text-green-600 hover:text-green-800 hover:bg-green-50',
          tooltip: t('campaigns:actions.resumeTooltip')
        });
        actions.push({
          label: t('campaigns:actions.close'),
          icon: StopIcon,
          action: () => handleStatusChange('completed'),
          className: 'text-red-600 hover:text-red-800 hover:bg-red-50',
          tooltip: t('campaigns:actions.closeTooltip')
        });
        break;

      default:
        return null;
    }

    return actions;
  };

  // Check permissions instead of userType
  const canApplyToCampaigns = hasPermission('matches:create');
  const canApply = canApplyToCampaigns && currentStatus === 'active';
  const statusActions = getStatusActions();

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden">
      {/* Campaign Image */}
      <div className="relative h-48 overflow-hidden">
        {/* Campaign image or gradient background */}
        {(campaign.representativeImages?.[0]?.fileUrl || campaign.imageUrl) ? (
          <img 
            src={campaign.representativeImages?.[0]?.fileUrl || campaign.imageUrl} 
            alt={campaign.campaignName || campaign.title || ''}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600"></div>
        )}
        
        {/* Overlay for better text readability when using image */}
        {(campaign.representativeImages?.[0]?.fileUrl || campaign.imageUrl) && (
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        )}

        {/* Placeholder pattern/image */}
        {!(campaign.representativeImages?.[0]?.fileUrl || campaign.imageUrl) && (
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '30px 30px'
            }} />
          </div>
        )}
        
        {/* Category badge overlay */}
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-gray-800 text-sm font-medium rounded-full shadow-sm">
            {campaign.campaignCategory || campaign.category || ''}
          </span>
        </div>
        
        {/* Status badge overlay */}
        <div className="absolute top-4 right-4">
          {currentStatus === 'stopped' ? (
            <span className="px-3 py-1 backdrop-blur-sm text-sm font-bold rounded-full shadow-sm bg-[#0F1E3D] text-white">
              {t('campaigns:status.stopped', '종료됨')}
            </span>
          ) : (
            <span className={`px-3 py-1 backdrop-blur-sm text-sm font-medium rounded-full shadow-sm ${getStatusColor(currentStatus)}`}>
              {currentStatus?.charAt(0).toUpperCase() + currentStatus?.slice(1)}
            </span>
          )}
        </div>

        {/* Dim overlay for stopped campaigns */}
        {currentStatus === 'stopped' && (
          <div className="absolute inset-0 bg-white/50" />
        )}

        {/* Campaign title overlay at bottom */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
          <h3 className="text-xl font-bold text-white truncate" title={campaign.campaignName || campaign.title || ''}>
            {campaign.campaignName || campaign.title || ''}
          </h3>
        </div>
      </div>

      {/* Card Content */}
      <div className="p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <TruncatedText
            text={campaign.productIntroduction || campaign.description || ''}
            maxLines={3}
            maxLength={200}
            className="text-gray-600 text-sm"
            showTooltip={true}
          />
        </div>
      </div>

      {/* Status Actions for Campaign Owners */}
      {statusActions && statusActions.length > 0 && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 font-medium">{t('campaigns:campaignActions')}:</span>
            <div className="flex space-x-2">
              {statusActions.map((action, index) => {
                const IconComponent = action.icon;
                return (
                  <button
                    key={index}
                    onClick={action.action}
                    disabled={updating}
                    title={action.tooltip}
                    className={`px-3 py-1 text-xs font-medium rounded-md border transition-colors ${action.className} ${
                      updating ? 'opacity-50 cursor-not-allowed' : 'border-current'
                    }`}
                  >
                    <div className="flex items-center space-x-1">
                      <IconComponent className="h-3 w-3" />
                      <span>{updating ? t('campaigns:updating') : action.label}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        {campaign.brandName && (
          <div className="flex items-center space-x-2 min-w-0">
            <BanknotesIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span className="text-sm text-gray-600 truncate">{campaign.brandName}</span>
          </div>
        )}

        {campaign.recruitmentPeriod ? (
          <div className="flex items-center space-x-2 min-w-0">
            <CalendarDaysIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span className="text-sm text-gray-600 truncate">
              {campaign.recruitmentPeriod.alwaysRecruiting
                ? t('campaigns:registration.fields.alwaysRecruiting')
                : campaign.recruitmentPeriod.endDate
                  ? `${formatDate(campaign.recruitmentPeriod.startDate, { month: 'short', day: 'numeric' })} - ${formatDate(campaign.recruitmentPeriod.endDate, { month: 'short', day: 'numeric' })}`
                  : formatDate(campaign.recruitmentPeriod.startDate, { month: 'short', day: 'numeric' })}
            </span>
          </div>
        ) : campaign.timeline?.startDate ? (
          <div className="flex items-center space-x-2 min-w-0">
            <CalendarDaysIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span className="text-sm text-gray-600 truncate">
              {formatDate(campaign.timeline.startDate, { month: 'short', day: 'numeric' })}
              {campaign.timeline.endDate && ` - ${formatDate(campaign.timeline.endDate, { month: 'short', day: 'numeric' })}`}
            </span>
          </div>
        ) : null}

        {campaign.numberOfRecruits != null && (
          <div className="flex items-center space-x-2 min-w-0">
            <UserGroupIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span className="text-sm text-gray-600 truncate">
              {formatNumber(campaign.numberOfRecruits)} {t('campaigns:registration.fields.numberOfRecruits')}
            </span>
          </div>
        )}
      </div>

      {/* Content Types / Tags */}
      <div className="mb-4">
        <div className="flex flex-wrap gap-2">
          {campaign.contentTypes && campaign.contentTypes.length > 0 ? (
            campaign.contentTypes.slice(0, 4).map((ct: string, idx: number) => (
              <span key={idx} className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full whitespace-nowrap">
                {ct}
              </span>
            ))
          ) : campaign.requirements?.platforms && campaign.requirements.platforms.length > 0 ? (
            campaign.requirements.platforms.slice(0, 3).map((p: string, idx: number) => (
              <span key={idx} className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full whitespace-nowrap">
                {p}
              </span>
            ))
          ) : null}
          {campaign.campaignType && (
            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full whitespace-nowrap">
              {t(`campaigns:registration.campaignTypes.${campaign.campaignType}`) || campaign.campaignType}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4 text-sm text-gray-500 min-w-0 flex-1">
          <span className="flex items-center min-w-0" title={campaign.createdAt ? formatDate(campaign.createdAt, { year: 'numeric', month: 'short', day: 'numeric' }) : 'Recently'}>
            <ClockIcon className="h-4 w-4 mr-1 flex-shrink-0" />
            <span className="truncate">{campaign.createdAt ? formatDate(campaign.createdAt, { year: 'numeric', month: 'short', day: 'numeric' }) : 'Recently'}</span>
          </span>
          {campaign.location && (
            <span className="flex items-center min-w-0" title={campaign.location}>
              <MapPinIcon className="h-4 w-4 mr-1 flex-shrink-0" />
              <span className="truncate">{campaign.location}</span>
            </span>
          )}
        </div>

        <div className="flex space-x-2 flex-shrink-0 ml-4">
          {/* View Details Button - Always show */}
          <button
            onClick={() => onViewDetails?.(campaign)}
            className="px-4 py-2 text-blue-600 hover:text-blue-800 text-sm font-medium border border-blue-600 rounded-md hover:bg-blue-50 transition-colors whitespace-nowrap"
          >
            {t('campaigns:viewDetails')}
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};
